import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useState } from 'react';

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';

export type OpcionClase = components['schemas']['ClassOption'];

function useOpcionesClase(q: string, id: number | null) {
  return useQuery({
    queryKey: ['contenido', 'clases', 'opciones', q, id],
    queryFn: () =>
      unwrap<OpcionClase[]>(
        api.GET('/api/v1/admin/classes/options', {
          params: { query: id !== null ? { id } : { q } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** Elegir una clase por nombre (búsqueda) en lugar de escribir su número. */
export function SelectorClase({
  valor,
  onCambio,
  deshabilitado = false,
  etiqueta = 'Clase',
}: {
  valor: number | null;
  onCambio: (id: number | null, clase: OpcionClase | null) => void;
  deshabilitado?: boolean;
  etiqueta?: string;
}) {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const actual = useOpcionesClase('', valor);
  const lista = useOpcionesClase(texto.trim(), null);
  const elegida = valor !== null ? actual.data?.find((c) => c.id === valor) : undefined;

  if (valor !== null) {
    return (
      <div className="input-vidrio flex items-center gap-2">
        <span className="flex-1 truncate">
          {elegida ? (
            <>
              <span className="font-semibold">{elegida.name}</span>{' '}
              <span className="text-xs text-texto-3">
                #{elegida.id}
                {elegida.specialtyName && ` · ${elegida.specialtyName}`}
              </span>
            </>
          ) : (
            `Clase #${valor}`
          )}
        </span>
        {!deshabilitado && (
          <button
            type="button"
            aria-label={`Quitar ${etiqueta.toLowerCase()}`}
            className="text-texto-3 hover:text-texto"
            onClick={() => onCambio(null, null)}
          >
            <X size={16} aria-hidden />
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="relative">
      <input
        className="input-vidrio"
        aria-label={etiqueta}
        placeholder="Busca la clase por nombre o número"
        value={texto}
        disabled={deshabilitado}
        onFocus={() => setAbierto(true)}
        onBlur={() => setTimeout(() => setAbierto(false), 150)}
        onChange={(e) => {
          setTexto(e.target.value);
          setAbierto(true);
        }}
      />
      {abierto && lista.data && (
        <ul className="panel-vidrio absolute z-20 mt-1 max-h-64 w-full overflow-y-auto p-1 shadow-lg">
          {lista.data.length === 0 && <li className="p-2 text-sm text-texto-3">Sin resultados</li>}
          {lista.data.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-[var(--pista)]"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onCambio(c.id, c);
                  setTexto('');
                  setAbierto(false);
                }}
              >
                <span className="font-semibold">{c.name}</span>{' '}
                <span className="text-xs text-texto-3">
                  #{c.id}
                  {c.specialtyName && ` · ${c.specialtyName}`}
                  {c.isWorkshop && ' · taller'}
                  {!c.isPublished && ' · no visible'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
